// The footer, logo and slide number kept legible on a background picture (SA, group 0; David: "hard to read on a busy
// background picture"). The part of the picture behind each corner's items is read once, small, from its pixels as the
// slide draws it (filling or fitting, with its veil): how light it is and how busy (how much its lightness varies).
// On a calm patch the words take light or dark ink, whichever reads; on a busy one they sit on a soft backing.

import { useEffect, useState } from "react";
import { picturePath } from "../../../../shared/imageInfo";
import type { SlideBg } from "../../../../shared/slideDesign";

export type Spot = "tl" | "tr" | "bl" | "br" | "bc";

/** Where each corner's items sit on the 1600 by 900 slide, as a share across and down (the patch read behind them). */
const PATCH: Record<Spot, [number, number, number, number]> = {
  tl: [0.04, 0.02, 0.4, 0.14],
  tr: [0.56, 0.02, 0.96, 0.14],
  bl: [0.04, 0.86, 0.6, 0.98],
  br: [0.66, 0.86, 0.96, 0.98],
  bc: [0.3, 0.86, 0.7, 0.98],
};

export interface SpotRead {
  /** how light, 0 to 1 */
  tone: number;
  /** how much its lightness varies (a standard deviation, 0 to 0.5) */
  busy: number;
}

const READS = new Map<string, Promise<Record<Spot, SpotRead> | null>>();

/** Every corner's patch of a background picture, read once at 96 by 54 as the slide draws it. */
function readSpots(key: string, fit: "fill" | "fit"): Promise<Record<Spot, SpotRead> | null> {
  const id = `${key}|${fit}`;
  if (READS.has(id)) return READS.get(id)!;
  const p = new Promise<Record<Spot, SpotRead> | null>((done) => {
    if (typeof Image === "undefined" || typeof document === "undefined") return done(null);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const W = 96;
        const H = 54;
        const c = document.createElement("canvas");
        c.width = W;
        c.height = H;
        const g = c.getContext("2d", { willReadFrequently: true });
        if (!g) return done(null);
        // as the slide draws it: cover or contain, centred (a fitted picture's empty sides read as mid grey, unknown)
        g.fillStyle = "#808080";
        g.fillRect(0, 0, W, H);
        const s = fit === "fit" ? Math.min(W / img.naturalWidth, H / img.naturalHeight) : Math.max(W / img.naturalWidth, H / img.naturalHeight);
        const w = img.naturalWidth * s;
        const h = img.naturalHeight * s;
        g.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
        const d = g.getImageData(0, 0, W, H).data;
        const out = {} as Record<Spot, SpotRead>;
        for (const [spot, [x0, y0, x1, y1]] of Object.entries(PATCH) as [Spot, number[]][]) {
          let n = 0;
          let sum = 0;
          let sq = 0;
          for (let y = Math.floor(y0 * H); y < Math.ceil(y1 * H); y++)
            for (let x = Math.floor(x0 * W); x < Math.ceil(x1 * W); x++) {
              const i = (y * W + x) * 4;
              const l = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
              sum += l;
              sq += l * l;
              n++;
            }
          const tone = sum / n;
          out[spot] = { tone, busy: Math.sqrt(Math.max(0, sq / n - tone * tone)) };
        }
        done(out);
      } catch {
        done(null);
      }
    };
    img.onerror = () => done(null);
    img.src = picturePath(key);
  });
  READS.set(id, p);
  return p;
}

/** How the corners' items read on a slide's background: on dark or light, backed where it is busy (null: no picture). */
export function useSpotReads(bg: SlideBg | null | undefined): Record<Spot, SpotRead> | null {
  const key = bg?.kind === "picture" ? bg.image.key : null;
  const fit = bg?.kind === "picture" ? bg.fit : "fill";
  const veil = bg?.kind === "picture" ? (bg.veil ?? 0) : 0;
  const [reads, setReads] = useState<Record<Spot, SpotRead> | null>(null);
  useEffect(() => {
    if (!key) return setReads(null);
    let live = true;
    void readSpots(key, fit).then((r) => live && setReads(r));
    return () => {
      live = false;
    };
  }, [key, fit]);
  if (!reads) return null;
  if (!veil) return reads;
  // the veil darkens toward black or lightens toward white, and calms the patch as much
  const out = {} as Record<Spot, SpotRead>;
  for (const [s, r] of Object.entries(reads) as [Spot, SpotRead][]) out[s] = { tone: veil < 0 ? r.tone * (1 + veil) : r.tone + (1 - r.tone) * veil, busy: r.busy * (1 - Math.abs(veil)) };
  return out;
}

/** What a corner's items wear: ink for a dark or a light patch, and a soft backing on a busy one. */
export function spotLook(r: SpotRead | undefined): { dark: boolean; backed: boolean } | null {
  if (!r) return null;
  return { dark: r.tone < 0.55, backed: r.busy > 0.11 || (r.tone > 0.35 && r.tone < 0.65) };
}
